<template>
  <div
    v-if="!isMobile && !isTablet"
    class="single-obituary-navigation__actions"
  >
    <div
      v-if="order && !isLoading"
      class="single-obituary-navigation__actions-container"
      :class="!noDonationsAllowed ? 'donation-support' : null"
    >
      <button class="btn link" @click.prevent="initShare()">
        <span v-text="texts.sharePage" />
      </button>

      <button
        v-if="!noDonationsAllowed"
        class="btn outline"
        @click.prevent="toggleModal(DONATION_INITIAL)"
        v-text="texts.donate"
      />

      <button
        class="btn cta"
        v-if="!noGreetings"
        @click.prevent="toggleModal(FORM_INITIAL)"
        v-text="texts.writeCondolence"
      />
    </div>

    <div
      v-else-if="!order"
      class="single-obituary-navigation__actions-container"
    >
      <button class="btn link" @click.prevent="initShare()">
        <span v-text="texts.sharePage" />
      </button>

      <button
        class="btn cta"
        v-if="!noGreetings"
        @click.prevent="toggleModal(FORM_INITIAL)"
        v-text="texts.writeCondolence"
      />
    </div>
  </div>
</template>

<script>
import { useIsMobile } from 'Main/composables/useIsMobile';
import {
  getDonationFromLocalStorage,
  removeDonationFromLocalStorage,
} from '../helpers';
import { initShare } from 'Main/share';
import {
  getIntentFromUrl,
  getIntentModalContext,
  INTENT_GIFT,
  removeIntentFromUrl,
} from '../intent-links';
import {
  FORM_INITIAL,
  DONATION_INITIAL,
  FORM_QUERY,
  DONATION,
} from '../createCommentStepsHelper';
import { mapActions, mapMutations, mapState } from 'vuex';

export default {
  name: 'NavigationActions',

  setup() {
    const { isMobile, isTablet } = useIsMobile();
    return { isMobile, isTablet };
  },

  props: {
    orderId: {
      type: String,
      default: null,
    },
    noDonations: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      texts: lavendlaObituary.common,
      noGreetings: lavendlaObituary.noGreetings,
      FORM_INITIAL,
      DONATION_INITIAL,
    };
  },

  computed: {
    ...mapState('orders', ['order', 'isLoading']),
    ...mapState('donations', [
      'isDonating',
      'donation',
      'noDonationsAllowed',
      'hasDonated',
    ]),
    ...mapState('createComment', ['steps', 'comment', 'modalIsOpen']),
    ...mapState('comments', ['commentsList']),

    createGreetingText() {
      return this.texts.createComment.writeCondolenceShort + '…';
    },

    createGreetingWithMediaText() {
      return this.texts.createComment.addMediaShort;
    },
  },

  watch: {
    order(updatedOrderData) {
      if (updatedOrderData?.pennybridge_id) {
        this.setPennyBridgeId(updatedOrderData.pennybridge_id);
      }

      if (updatedOrderData?.memorialFund?.description) {
        this.setDescription(updatedOrderData.memorialFund.description);
      }
    },
  },

  created() {
    const intent = getIntentFromUrl();
    // Only a valid PennyBridge return (redirect=1 with a comment id) takes precedence.
    const { redirectFromPennyBridge, commentIdFromPennyBridge } =
      this.validatePennyBridgeQueryParams();
    const isPennyBridgeRedirect =
      redirectFromPennyBridge && commentIdFromPennyBridge !== null;
    removeIntentFromUrl();

    if (this.noDonations) {
      this.setNoDonationsAllowed(true);
    } else if (!this.donation) {
      this.maybeSetDonation();
    }

    if (this.orderId && !this.order) {
      this.$store.dispatch('orders/getOrder', this.orderId);
    }

    // The PennyBridge return flow decides which step to show, so it wins.
    if (intent && !isPennyBridgeRedirect && !this.noGreetings) {
      this.openIntentWhenReady(intent);
    }
  },

  methods: {
    ...mapMutations('donations', [
      'setDonation',
      'setPennyBridgeId',
      'setNoDonationsAllowed',
      'setDescription',
    ]),
    ...mapActions('createComment', [
      'toggleModal',
      'setDetailsFromDonation',
      'updateSteps',
    ]),
    ...mapMutations('creator', ['setState']),
    ...mapMutations('createComment', ['setModalContext']),
    ...mapActions('creator', ['publishPage']),
    ...mapActions('donations', ['checkDonationStatus', 'resetDonation']),
    ...mapActions('comments', ['updateNewCommentId']),
    getDonationFromLocalStorage,
    initShare,

    maybeSetDonation() {
      const donation = this.getDonationFromLocalStorage();
      if (!donation) {
        return;
      }

      const orderId = parseInt(this.orderId);

      if (parseInt(donation.obituaryId) === orderId) {
        this.setDonation(donation);
        removeDonationFromLocalStorage();

        return;
      }

      if (parseInt(donation.orderId) === orderId) {
        const { redirectFromPennyBridge, commentIdFromPennyBridge } =
          this.validatePennyBridgeQueryParams();

        if (!redirectFromPennyBridge || !commentIdFromPennyBridge) {
          removeDonationFromLocalStorage();

          return;
        }

        this.setDonation(donation);

        this.checkDonationStatus()
          .then((_) => {
            if (this.hasDonated) {
              if (this.donation.comment) {
                this.updateNewCommentId(this.donation.id);

                removeDonationFromLocalStorage();
              } else {
                this.setDetailsFromDonation(this.donation);
                this.updateSteps('donation');

                this.toggleModal(FORM_QUERY);
              }

              return;
            }

            this.resetDonation();
            this.toggleModal(DONATION);
          })
          .catch((e) => console.log(e));
      }

      this.maybeRemoveQueryParams();
    },

    openIntentWhenReady(intent) {
      // Same gate as the buttons: the gift button needs a loaded order.
      const open = () => {
        if (this.modalIsOpen) {
          return;
        }

        if (intent === INTENT_GIFT && (this.noDonationsAllowed || !this.order)) {
          return;
        }

        this.toggleModal(getIntentModalContext(intent));
      };

      if (!this.orderId || !this.isLoading) {
        open();

        return;
      }

      // A modal already open (e.g. a restored donation) keeps the intent from
      // opening once the visitor closes it.
      if (this.modalIsOpen) {
        return;
      }

      const unwatch = this.$watch('isLoading', (isLoading) => {
        if (!isLoading) {
          unwatch();
          open();
        }
      });
    },

    validatePennyBridgeQueryParams() {
      const { redirectFromPennyBridge, commentIdFromPennyBridge } =
        this.getPennyBridgeQueryParams();

      const result = {
        redirectFromPennyBridge: false,
        commentIdFromPennyBridge: null,
      };

      if (!redirectFromPennyBridge || !commentIdFromPennyBridge) {
        return result;
      }

      result.redirectFromPennyBridge =
        redirectFromPennyBridge &&
        !isNaN(redirectFromPennyBridge) &&
        parseInt(redirectFromPennyBridge) === 1;

      if (
        commentIdFromPennyBridge &&
        !isNaN(commentIdFromPennyBridge) &&
        parseInt(commentIdFromPennyBridge) >= 0
      ) {
        result.commentIdFromPennyBridge = parseInt(commentIdFromPennyBridge);
      }

      return result;
    },

    maybeRemoveQueryParams() {
      const { redirectFromPennyBridge, commentIdFromPennyBridge } =
        this.getPennyBridgeQueryParams();

      if (!redirectFromPennyBridge && !commentIdFromPennyBridge) {
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);

      if (redirectFromPennyBridge) {
        urlParams.delete('redirect_from_penny_bridge');
      }

      if (commentIdFromPennyBridge) {
        urlParams.delete('commentId');
      }

      let url = window.location.pathname;

      const remainingUrlParams = urlParams.toString();

      if (remainingUrlParams) {
        url += `?${remainingUrlParams}`;
      }

      window.history.replaceState({}, document.title, url);
    },

    getPennyBridgeQueryParams() {
      const urlParams = new URLSearchParams(window.location.search);

      return {
        redirectFromPennyBridge: urlParams.get('redirect_from_penny_bridge'),
        commentIdFromPennyBridge: urlParams.get('commentId'),
      };
    },
  },
};
</script>
